'use client'

import { ReactNode, useEffect, useMemo, useState } from 'react'

import {
  broadcast,
  BROADCAST_TRIGGER,
  SessionContext,
  SessionContextValue,
} from '@/shared/lib'
import type { Session } from '@/shared/lib/server'

type SessionProviderProps = {
  children: ReactNode
  session?: Session | null
}

type AuthClientConfig = {
  _session: Session | null | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _getSession: (...args: any[]) => any
}

type FetchSessionParams = {
  broadcast?: boolean
}

const fetchSession = async (
  params?: FetchSessionParams,
): Promise<Session | null> => {
  const response = await fetch('/api/auth/session', {
    method: 'GET',
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error('Failed to fetch session')
  }

  if (params?.broadcast ?? true) {
    broadcast().postMessage({
      event: 'session',
      data: { trigger: BROADCAST_TRIGGER.GET_SESSION },
    })
  }

  return response.json() as unknown as Session | null
}

export const __AUTH: AuthClientConfig = {
  _session: undefined,
  _getSession: () => {},
}

export const SessionProvider = ({
  children,
  session: initialSession,
}: SessionProviderProps) => {
  if (!SessionContext) {
    throw new Error('React Context is unavailable in Server Components')
  }

  const hasInitialSession = !!initialSession

  const [session, setSession] = useState<Session | null | undefined>(() => {
    if (hasInitialSession) {
      __AUTH._session = initialSession
    }

    return initialSession
  })
  const [loading, setLoading] = useState(!initialSession)

  useEffect(() => {
    __AUTH._getSession = async ({ event } = {}) => {
      try {
        const storageEvent = event === 'storage'

        // 클라이언트 세션이 아직 없는 경우 항상 세션을 조회한다.
        // or 다른 탭이나 윈도우에서 이벤트가 있는 경우 세션을 조회한다.
        if (__AUTH._session === undefined || storageEvent) {
          __AUTH._session = await fetchSession({
            broadcast: false,
          })
          setSession(__AUTH._session)

          return
        }

        if (!event || __AUTH._session === null) {
          return
        }

        __AUTH._session = await fetchSession()
        setSession(__AUTH._session)
      } catch (error) {
        console.error('Failed to fetch session:', error)
        setSession(null)
      } finally {
        setLoading(false)
      }
    }

    __AUTH._getSession()

    return () => {
      __AUTH._session = undefined
      __AUTH._getSession = () => {}
    }
  }, [])

  useEffect(() => {
    const handleBroadcastMessage = (event: Event) => {
      const messageEvent = event as MessageEvent

      __AUTH._getSession({ event: 'storage' })

      if (messageEvent.data.data.trigger === BROADCAST_TRIGGER.SIGN_OUT) {
        window.location.reload()
      }
    }

    broadcast().addEventListener('message', handleBroadcastMessage)

    return () => {
      broadcast().removeEventListener('message', handleBroadcastMessage)
    }
  }, [])

  const value: SessionContextValue = useMemo(() => {
    return {
      data: session,
      status: loading
        ? 'loading'
        : session
          ? 'authenticated'
          : 'unauthenticated',
      update: async () => {
        setLoading(true)

        try {
          const newSession = await fetchSession({
            broadcast: true,
          })

          if (newSession) {
            setSession(newSession)
          }
        } catch (error) {
          setSession(null)
        } finally {
          setLoading(false)
        }
      },
    }
  }, [session, loading])

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}
